'use client';
import { useSite, useT } from '../../lib/i18n';
import { pickName } from '../../lib/i18n-core';
import type { ProductListItem } from '../../lib/site';
import { Icon } from '../Icon';
import { WishButton } from './WishButton';
import { CourseLinks } from './CourseLinks';
import { PriceFooter, PriceOptions, usePrice } from './PriceBlock';
import { Thumb } from './Thumb';
import { badgeClass, typeLabel } from './labels';
// import { limitBadges } from './price'; // ⚠ 수강 제한 배지와 함께 걷어 둠(2026-08-26)
import { SmartLink } from '../SmartLink';

/**
 * 카드 한 장 — 가이드 `course-card`.
 *
 * **형태는 설정이 고른다**(`listStyle.listview_card`) —
 *  - `detail`  : 가이드 "카드형 디테일" (배지·맛보기·관련교재·차시/기간·가격·담기/신청)
 *  - `simple`  : 가이드 "카드형 심플"  (썸네일·찜·제목·가격만)
 * 가이드가 둘을 나란히 그려 둔 것은 견본이라, 화면에는 **하나만** 나온다.
 */
export function CourseCard({ p, variant, priceDetail, freeVod, hoverPreview, previewDelay, href, billing }: {
  p: ProductListItem;
  variant: 'detail' | 'simple';
  priceDetail: boolean;
  /** 맛보기 노출(설정) — 끄면 상품에 맛보기가 있어도 그리지 않는다 */
  freeVod: boolean;
  /** 미리보기 재생(설정 `listStyle.hoverPreview`) — OVP 마켓만 켤 수 있다 */
  hoverPreview?: boolean;
  /** 여는 데 필요한 머무름(ms) — 비우면 기본값. /ux-lab 이 재 보는 데만 쓴다 */
  previewDelay?: number;
  href: string;
  /** 구독 보기 — 가격을 지우고 [수강하기] 하나만 둔다(`PriceFooter.billing`) */
  billing?: boolean;
}) {
  const t = useT();
  const teacher = p.displayTeachers[0]?.teacher;
  const price = usePrice(p, priceDetail);
  /*
   * 호버 미리보기는 **맛보기 0번째**를 쓴다(`previews[0]`, 맛보기 정렬 순 — 사용자 확정).
   * 대표를 바꾸려면 관리자에서 맛보기 정렬을 조정한다.
   */
  const previewCcid = hoverPreview ? p.previews[0]?.curriculumId : undefined;
  /*
   * 할인 표기는 **썸네일 배지**로 올린다(2026-08-22 사용자 지적으로 자리 옮김).
   * 푸터의 금액은 수강신청 버튼과 한 줄이어야 하는데, 296px 카드에서 원가·판매가·할인
   * 셋을 넣으면 백만 단위에서 어떤 글자 크기로도 들어가지 않는다. 배지로 올리면 자리도
   * 나고 눈에 더 잘 띈다 — 추천 배지와 같은 줄, 같은 문법이다.
   */

  if (variant === 'simple') {
    return (
      <div className="course-card">
        <Thumb image={p.image} name={p.name} href={href} previewCcid={previewCcid} previewDelay={previewDelay}>
          <WishButton productId={p.id} wished={p.wished} />
        </Thumb>
        <div className="course-body">
          <p className="course-title"><SmartLink href={href}>{p.name}</SmartLink></p>
          {/*
            간단형 푸터에는 값만 있다(가이드 "카드형 심플") — 담기·신청 버튼도 할인 표기도 없다.
            단가 세분화를 쓰는 마켓은 값 하나가 아니라 **종류별 값을 나열**한다(가이드 카드4).
          */}
          <div className="course-footer">
            <PriceFooter billing={billing} p={p} line={price.line} href={href} actions={false} priceOnly lines={price.lines}
              showSingle={priceDetail} />
          </div>
        </div>
      </div>
    );
  }

  /*
   * **교재는 카드가 다르다**(가이드 `book-list.html` — 2026-08-22 사용자 지적).
   * 강좌 카드를 그대로 쓰고 있었는데 가이드는 따로 그려 뒀다. 다른 점 넷 —
   *  ① 겉 클래스가 `book-card` ② 배지가 종류가 아니라 **과목(분류)**
   *  ③ 강사 대신 **저자**(`… 저`)와 교재 칩(개별구매·배송비무료)
   *  ④ 버튼이 `수강신청` 이 아니라 **`구매하기`**
   * 맛보기·차시/기간은 교재에 없다(가이드에도 없다).
   */
  if (p.type === 'BOOK') return <BookCard p={p} priceDetail={priceDetail} href={href} billing={billing} />;

  return (
    <div className="course-card">
      <Thumb image={p.image} name={p.name} href={href} previewCcid={previewCcid} previewDelay={previewDelay}>
{/* 추천 배지(BEST·NEW…)는 그리지 않는다 — 2026-08-23 사용자 지시 */}
        {/*
          썸네일 배지에는 **종류만** 둔다(2026-08-25 가이드 개정 반영).
          할인 표기는 가이드가 푸터의 `course-discount` 로 옮겼다 — 자리가 없어 배지로
          올렸던 것을 `course-price-wrap`(flex·wrap)이 해결했다.
        */}
        <div className="course-badges">
          <span className={`badge-type ${badgeClass(p.type)} filter`}>{typeLabel(p.type, t)}</span>
        </div>
        <WishButton productId={p.id} wished={p.wished} />
      </Thumb>
      <div className="course-body">
        {/*
         * 가이드 카드에는 분류·강사 줄이 없다 — 현행 카드가 분류(`slocation`)를 적었지만
         * 디자인이 그 줄을 빼기로 한 것이므로 가이드를 따른다(리스트에는 있다).
         */}
        <p className="course-title"><SmartLink href={href}>{p.name}</SmartLink></p>
        {/*
          맛보기·관련교재는 **칩 줄 안**에 배지로 온다(2026-08-26 가이드 개정).
          전에는 썸네일 위 반투명 띠(`course-links-wrap`)에 얹혀 있었는데, 그림을 가리고
          손가락으로 누르기도 어려웠다. 이제 차시·기간 배지와 한 줄에서 흐른다 —
          리스트형이 이미 쓰던 모양(`badge badge-light course-link`)과 같아졌다.
        */}
        <CourseChips p={p}>
          <CourseLinks p={p} freeVod={freeVod} teacherName={teacher?.name} iconOnly />
        </CourseChips>
        {/* 단가 고르기는 푸터 **앞**에 온다(가이드 카드 4) */}
        <PriceOptions p={p} lines={price.lines} picked={price.picked} onPick={price.setPicked}
          showSingle={priceDetail} />
        <div className="course-footer">
          {/* 할인 표기는 위 배지로 올렸다 — 여기는 원가+판매가만(버튼과 한 줄) */}
          <PriceFooter billing={billing} p={p} line={price.line} href={href} />
        </div>
      </div>
    </div>
  );
}

/**
 * 교재 카드 — 가이드 `book-list.html` 의 `book-card`.
 *
 * 강좌 카드와 뼈대는 같지만(썸네일·찜·제목·가격·버튼) 담기는 것이 다르다.
 * 없는 값은 그리지 않는다 — 저자가 없으면 그 줄이 없고, 칩도 켜진 것만 나온다.
 */
function BookCard({ p, priceDetail, href, billing }: { p: ProductListItem; priceDetail: boolean; href: string; billing?: boolean }) {
  const t = useT();
  const { lang } = useSite();
  const price = usePrice(p, priceDetail);
  const author = p.displayTeachers[0]?.teacher?.name;
  // 배지는 **과목**이다(가이드의 이론·실무·법규) — 가장 깊은 분류를 쓴다
  const subject = p.categories.length ? pickName(p.categories[p.categories.length - 1].name, lang) : null;

  return (
    <div className="book-card">
      {/* 할인 표기는 푸터의 `course-discount` 로 갔다(2026-08-25 가이드 개정 — 강좌와 같다) */}
      <Thumb image={p.image} name={p.name} href={href}>
        <WishButton productId={p.id} wished={p.wished} />
      </Thumb>
      <div className="course-body">
        {subject && (
          <div className="d-flex ai-center gap5 mb5">
            <span className="badge-type bg-blue filter">{subject}</span>
          </div>
        )}
        <p className="course-title"><SmartLink href={href}>{p.name}</SmartLink></p>
        {author && <p className="course-teacher"><Icon name="user" /> {t('prd.author', { name: author })}</p>}
        <BookChips p={p} />
        <div className="course-footer">
          {/* 할인은 썸네일 배지로 올렸으니 금액 옆에서는 뺀다 — 강좌 카드와 같다(2026-08-23) */}
          <PriceFooter billing={billing} p={p} line={price.line} href={href} buyLabel={t('prd.buy')} />
        </div>
      </div>
    </div>
  );
}

/** 교재 칩 — 켜 둔 것만(가이드의 `단독구매가능` → 개별구매가능·배송비무료) */
export function BookChips({ p, className }: { p: ProductListItem; className?: string }) {
  const t = useT();
  if (!p.bookSoloSale && !p.bookFreeShipping) return null;
  return (
    <div className={`course-chips${className ? ` ${className}` : ''}`}>
      {p.bookSoloSale && <span className="badge badge-light">{t('prd.book.soloSale')}</span>}
      {p.bookFreeShipping && <span className="badge badge-dark">{t('prd.book.freeShipping')}</span>}
    </div>
  );
}

/**
 * 차시·기간·수강 제한 배지 — 가이드 `course-chips`.
 * 교재는 차시·기간이 없다(현행도 `ptype != 'B'` 로 감쌌다).
 */
export function CourseChips({ p, className, children }: {
  p: ProductListItem;
  className?: string;
  /** 칩 줄 안에 함께 흐르는 것 — 카드형의 맛보기·관련교재 링크(2026-08-26 가이드 개정) */
  children?: React.ReactNode;
}) {
  const t = useT();
  if (p.type === 'BOOK') return null;

  const period = p.periodType === 'DATE'
    ? (p.periodEndDate ? p.periodEndDate.slice(0, 10) : null)
    : (p.periodDays ? t('prd.days', { n: p.periodDays }) : null);
  const lessons = p.totalLessons ? t('prd.lessons', { n: p.totalLessons }) : null;
  const head = [lessons, period].filter(Boolean).join(' / ');

  /*
   * ⚠ 수강 제한 배지(1시간·2배·10차시…) — **잠시 걷어 둔다**(2026-08-26 사용자 지시).
   * 되살리려면 아래 두 곳의 주석을 풀고 `limitBadges` import 도 함께 살린다.
   * 값을 만드는 `limitBadges`(price.ts)는 그대로 둔다 — 규칙이 사라진 것이 아니다.
   */
  // const limits = limitBadges(p, t);

  // 배지가 하나도 없어도 **링크가 있으면 줄을 그린다** — 링크가 갈 자리가 여기뿐이다
  if (!head && !children) return null;

  return (
    <div className={`course-chips${className ? ` ${className}` : ''}`}>
      {head && <span className="badge badge-light">{head}</span>}
      {/* {limits.map((x) => <span key={x} className="badge badge-dark">{x}</span>)} */}
      {children}
    </div>
  );
}
